import React from 'react';
import { Chip, type ChipProps } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';
import WarningIcon from '@mui/icons-material/Warning';

const STATUS_CONFIG: Record<string, { label: string; color: ChipProps['color']; icon: React.ReactElement }> = {
  CONVERGED: { label: 'Synced', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  FAILED: { label: 'Failed', color: 'error', icon: <ErrorIcon fontSize="small" /> },
  DRIFTED: { label: 'Drift detected', color: 'warning', icon: <WarningIcon fontSize="small" /> },
  IN_PROGRESS: { label: 'Running', color: 'warning', icon: <SyncIcon fontSize="small" /> },
  // Applied work still moving in AWS: in progress, never Synced (FE-14).
  SETTLING: { label: 'Settling', color: 'info', icon: <SyncIcon fontSize="small" /> },
  PENDING: { label: 'Pending', color: 'default', icon: <HourglassEmptyIcon fontSize="small" /> },
  SUCCEEDED: { label: 'Succeeded', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  RUNNING: { label: 'Running', color: 'warning', icon: <SyncIcon fontSize="small" /> },
  ABORTED: { label: 'Aborted', color: 'default', icon: <ErrorIcon fontSize="small" /> },
  TIMED_OUT: { label: 'Timed Out', color: 'error', icon: <ErrorIcon fontSize="small" /> },
  HEALTHY: { label: 'Healthy', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  IDLE: { label: 'Idle', color: 'default', icon: <HourglassEmptyIcon fontSize="small" /> },
  DEGRADED: { label: 'Degraded', color: 'warning', icon: <WarningIcon fontSize="small" /> },
  DOWN: { label: 'Down', color: 'error', icon: <ErrorIcon fontSize="small" /> },
  UNKNOWN: { label: 'Unknown', color: 'default', icon: <HelpOutlineIcon fontSize="small" /> },
  // Baseline stacks. REFUSED keeps the warning icon rather than the error one:
  // it is a deliberate halt on a change we will not make unattended, not a
  // failure, and the two need a person for different reasons.
  NOT_STARTED: { label: 'Not started', color: 'default', icon: <HourglassEmptyIcon fontSize="small" /> },
  APPLYING: { label: 'Applying', color: 'warning', icon: <SyncIcon fontSize="small" /> },
  // Amber, not red. A refusal is OpsPilot declining to make a change it will
  // not make unattended: a condition for someone to deal with rather than a
  // fault, and four of them on one account should not read as four alarms.
  REFUSED: { label: 'Refused', color: 'warning', icon: <WarningIcon fontSize="small" /> },
  BLOCKED: { label: 'Blocked', color: 'warning', icon: <ErrorIcon fontSize="small" /> },
  // Held and unwanted: in the account, desired by nobody, and untouched by
  // any tick until a person removes it.
  NO_LONGER_DESIRED: { label: 'Not wanted', color: 'warning', icon: <WarningIcon fontSize="small" /> },
  // One baseline of an account, rolled up over its stacks. APPLYING, REFUSED
  // and BLOCKED above already read right for a baseline.
  IN_SYNC: { label: 'In sync', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  MISSING: { label: 'Missing', color: 'default', icon: <HourglassEmptyIcon fontSize="small" /> },
  WAITING_ON_DEPENDENCY: { label: 'Waiting on dependency', color: 'default', icon: <HourglassEmptyIcon fontSize="small" /> },
  // Off is a decision, not a wait, so not the hourglass.
  OFF: { label: 'Off', color: 'default', icon: <RemoveCircleOutlineIcon fontSize="small" /> },
};

export interface AsyncStatusChipProps {
  status: string;
  size?: 'small' | 'medium';
}

const StatusChip: React.FC<AsyncStatusChipProps> = ({ status, size = 'small' }) => {
  const config = STATUS_CONFIG[status] ?? {
    label: status,
    color: 'default' as const,
    icon: <HourglassEmptyIcon fontSize="small" />,
  };

  return (
    <Chip
      label={config.label}
      color={config.color}
      icon={config.icon}
      size={size}
      variant="outlined"
    />
  );
};

export default StatusChip;
